<template>
  <!--begin::General Widget 1-->
  <div>
    <v-data-table
      :headers="tableHeaders"
      :items="processedItems"
      item-key="ItemId"
      class="elevation-1"
      :loading="loading"
      :custom-sort = "customSort"
    >
      <template v-slot:[`item.name`]="{ item }">
        <ItemSummary :item="item" />
      </template>
      <template v-slot:[`item.ProhibitReason`]="{ item }">
        {{ getProhibitReasonLabel(item.ProhibitReason) }}
      </template>
      <template v-slot:[`item.Date`]="{ item }">
        {{ formatDate(item.Date) }}
      </template>
      <template v-slot:[`item.action`]="{ item }">
        <span v-if="item.Option">
          {{ getResolutionOptionText(item.Option) }}
        </span>
        <ItemResolution
          v-else-if="!showAdminOption"
          :itemId="item.ItemId"
          :isPermitted="item.Permitted"
          @updated="getItems"
        />
      </template>
      <template v-slot:[`item.resolve`]="{ item }">
        <span v-if="item.OptionConfirmed" class="text-green">Resolved</span>
        <button
          v-else-if="!item.OptionConfirmed && item.Option && showAdminOption"
          class="btn btn-primary btn-sm"
          @click="
            itemId = item.ItemId;
            resolution = item.Option;
            setCompleteConfirmation();
          "
        >
          Resolve
        </button>
      </template>
    </v-data-table>
    <ConfirmationModal
      :isLoadingConfirmation="loadingConfirmation"
      :isShowDialog="isShowConfirmationModal"
      :btnConfig="buttonConfiguration"
      :inputInfo="confirmationModalInfo"
      :inputColor="confirmationModalInfoColor"
      @eConfirm="completeOption"
      @eCancel="defaultCancelOperation"
    />
    <SnackbarNotifier
      :isShow="isShowSnackBar"
      :message="responseMessage"
      :snackType="snackbarType"
      :colour="snackBarColour"
      @eReset="resetSnackBar"
    />
  </div>
  <!--end::General Widget 1-->
</template>
<script>
import dayjs from "dayjs";
import { ApiService } from "@/services/ApiService";
import { useStore } from "@/store";
import { error } from "@/services/LoggerService";
import ConfirmationModal from "@/components/ConfirmationModal.vue";
import ConfirmationModalMixin from "@/mixins/ConfirmationModalMixins";
import SnackbarNotifier from "@/components/SnackbarNotifier.vue";
import ItemSummary from "@/components/ItemSummary.vue";
import ItemResolution from "@/components/ItemResolution.vue";
import SnackbarNotifierMixin from "@/mixins/SnackbarNotifierMixins";
import mixins from "vue-typed-mixins";
import resolutionOptions from "@/config/resolution-options.json";
import { getProhibitedCategoryLabel } from "@/services/ProhibitedCategoriesService";

export default mixins(SnackbarNotifierMixin, ConfirmationModalMixin).extend({
  name: "CustomerItemTable",
  props: { customerItems: { type: Object }, pybNumber: { type: String } },
  data() {
    return {
      loading: false,
      items: [],
      itemId: "",
      resolution: "",
      expanded: [],
      tableHeaders: [
        { text: "Tracking Number", value: "TrackingNumber" },
        { text: "Description", value: "name" },
        { text: "Category", value: "Description" },
        { text: "Courier", value: "Courier" },
        { text: "Status", value: "ProhibitReason" },
        { text: "Action", value: "action" },
        { text: "Received Date", value: "Date" },
        { text: "Resolve", value: "resolve" },
      ],
    };
  },
  components: {
    ConfirmationModal,
    SnackbarNotifier,
    ItemSummary,
    ItemResolution,
  },
  computed: {
    processedItems() {
      if (this.customerItems) return this.customerItems.items;
      if (this.pybNumber) {
        return this.items;
      }
      return [];
    },
    resolutionList() {
      return resolutionOptions;
    },
    isAdmin() {
      const groups = useStore().getters.getGroups;
      return groups.includes("Admin") || groups.includes("Worker");
    },
    isAdminPage() {
      return this.$route.name == "Admin";
    },
    showAdminOption() {
      return this.isAdmin && this.isAdminPage;
    },
  },
  watch: {
    async pybNumber(value) {
      await this.getItems();
    },
  },
  methods: {
    getProhibitReasonLabel(value){
      return getProhibitedCategoryLabel(value);
    },
    formatDate(date) {
      return dayjs(date).format("D-MMM-YYYY");
    },
    setCompleteConfirmation() {
      const resolutionLabel =
        this.getResolutionOptionText(this.resolution) ?? this.resolution;

      this.showConfirmationModal({
        title: `Confirm`,
        message: `Are you sure you want to mark this resolved via<b><em> ${resolutionLabel}</em></b>?`,
      });
    },
    getResolutionOptionText(option) {
      if (!option) return "";

      return this.resolutionList.find((r) => r.action === option)?.label ?? "";
    },
    async getItems() {
      try {
        this.loading = true;
        const pybNumber = this.pybNumber ?? this.customerItems.pybNumber;
        const result = await ApiService.Instance.getCustomerItems(pybNumber);
        if (result) this.items = result.Items;
        this.loading = false;
      } catch (e) {
        this.loading = false;
        error(e);
      }
    },
    async completeOption() {
      try {
        const pybNumber = this.pybNumber ?? this.customerItems.pybNumber;
        this.startConfirmationLoading();
        const result = await ApiService.Instance.confirmCustomerItemOption({
          pybNumber,
          itemId: this.itemId,
        });
        if (result)
          if (result) {
            this.showNotifier({
              message: `Successfully Resolved`,
              snackbarType: "success",
            });
            this.$emit("update");
            await this.getItems();
          }
      } catch (e) {
        error(e);
        this.showNotifier({
          message: `Error when Resolving`,
          snackbarType: "success",
        });
      }
      this.stopConfirmationLoading();
    },
    refresh() {},

    customSort(items, sortBy, sortDesc){
      const sortDirection = sortDesc[0]
      const rawKey = sortBy[0]

      const sortDict = {
        "action": "Option",
        "name": "ItemName",
        "resolve": "OptionConfirmed"
      }

      let sortKey = sortDict[rawKey]
      if(!sortKey){
        sortKey = rawKey
      }

      return items.sort((a,b)=>{
        let aValue = a[sortKey]
        let bValue = b[sortKey]


          return this.basicSort(aValue,bValue,sortDirection)
      })
    },

    basicSort(a,b, descending){
      if(typeof (a) == "string"){
        a = a.toLowerCase()
      }
      if(typeof (b) == "string"){
        b = b.toLowerCase()
      }
      if(!a){
        a=""
      }
      if(!b){
        b=""
      }

      let output = 0
      if (a<b){
        output = -1
      } else if (a>b){
        output = 1
      }
      if(descending){
        output*=-1
      }
      return output

    }
  },
  async beforeMount() {
    try {
      if (this.pybNumber) await this.getItems();
    } catch (e) {
      error(e);
    }
  },
});
</script>